import { useCallback, useEffect, useRef, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useLocation, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import emitter from "../../Utils/events/eventBus";
import SetupCard from "../Dashboard/components/SetupCard";
import CustomFilterTabs from "../../Components/CustomFilterTabs";
import WeeklyTasks from "./components/WeeklyTasks";
import ShareCard from "./components/ShareCard";
import ShareDialog from "./components/ShareDialog";
import ReplyHelper from "./components/ReplyHelper";
import AnnouncementTool from "./components/AnnouncementTool";
import PreviewSwitcher from "./components/PreviewSwitcher";
import SectionCard from "./components/SectionCard";
import { useShareItems } from "./hooks/useGrowCommunity";
import { SHARE_FILTERS, nextResetDate } from "./mockData";
import { REPLY_HELPER_ID, REVEAL_EVENT, SHARE_GRID_ID, TASKS_ID } from "./sections";
import { g, PURPLE_TINT, TEXT_SUB } from "./styles";
import { formatDateToDDMYY } from "../../Utils/dateFormat";
import { bidiIsolate } from "../../Helpers/formating";

const s = {
  shareList: { display: "flex", flexDirection: "column", gap: "8px" },
  tabs: { width: "100%", minWidth: 0 },
  // Keeps headings clear of the mobile top bar when scrolled to.
  anchor: { scrollMarginTop: { xs: "88px", md: "16px" } },
  // When this week's list is replaced. Same size as the sidebar's preview/soon
  // badges; on a narrow card the short form keeps it on the title line.
  resetBadge: {
    display: "inline-flex",
    alignItems: "center",
    height: "15px",
    padding: "0 6px",
    borderRadius: "12px",
    fontSize: "10px",
    fontWeight: 500,
    color: TEXT_SUB,
    backgroundColor: PURPLE_TINT,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "& .reset-short": { display: "none" },
    "@container (max-width: 400px)": {
      "& .reset-full": { display: "none" },
      "& .reset-short": { display: "inline" },
    },
  },
};

// Which accordion holds each section id (mobile). The AI column's anchor keeps
// the reply tool's id, so tasks that point at "saved answers" still land there.
const SECTION_KEYS = { [TASKS_ID]: "tasks", [SHARE_GRID_ID]: "share", [REPLY_HELPER_ID]: "ai" };
const AI_TOOLS = ["reply", "announce"];

// DD/MM/YYYY in both languages, isolated so it never flips inside the sentence.
const formatResetDate = (date) => bidiIsolate(formatDateToDDMYY(date));

// "Grow your community" (קידום הקהילה) — CM only, scoped to the current
// community. Three columns, one job each: what to do this week, what to share,
// and writing with AI (reply to a question / a message to the community — new
// AI tools become more tabs there, so the layout doesn't change). On mobile
// each column is an accordion (only the tasks open by default). FE-first:
// everything reads from the mock hooks.
const GrowCommunityContainer = () => {
  const { t, i18n } = useTranslation("page-grow-community");
  const lang = (i18n.language || "he").startsWith("he") ? "he" : "en";
  const isRTL = lang === "he";
  const theme = useTheme();
  // Same switch the app uses for its mobile layout (sidebar becomes a drawer).
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { hash } = useLocation();
  const [searchParams] = useSearchParams();
  const { items, markShared } = useShareItems();
  const [shareItemId, setShareItemId] = useState(null);
  const [filterKey, setFilterKey] = useState("all");
  const [aiTool, setAiTool] = useState("reply");
  const [open, setOpen] = useState({ tasks: true, share: false, ai: false });

  const openRef = useRef(open);
  openRef.current = open;
  const isMobileRef = useRef(isMobile);
  isMobileRef.current = isMobile;
  const pendingReveal = useRef(null);

  const toggle = (key) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  // Something pointed at a section: bring it into view. On mobile a closed
  // accordion opens first, and the scroll waits until it has finished opening
  // (Collapse onEntered) — any earlier and the page is still too short.
  const reveal = useCallback((id) => {
    const key = SECTION_KEYS[id];
    if (!key) return;
    if (id === REPLY_HELPER_ID) setAiTool("reply");
    if (isMobileRef.current && !openRef.current[key]) {
      pendingReveal.current = id;
      setOpen((prev) => ({ ...prev, [key]: true }));
    } else {
      document.getElementById(id)?.scrollIntoView({ block: "start" });
    }
  }, []);

  const handleEntered = (id) => {
    if (pendingReveal.current !== id) return;
    pendingReveal.current = null;
    document.getElementById(id)?.scrollIntoView({ block: "start" });
  };

  useEffect(() => {
    emitter.on(REVEAL_EVENT, reveal);
    return () => emitter.off(REVEAL_EVENT, reveal);
  }, [reveal]);

  // Arriving from a task on another page (…/grow-community#share-grid).
  useEffect(() => {
    const id = hash.replace("#", "");
    if (id) reveal(id);
  }, [hash, reveal]);

  const shareItem = items.find((item) => item.id === shareItemId) || null;

  // Type tabs: only the ones with items this week, each with its count.
  const filters = SHARE_FILTERS.map((filter) => ({
    ...filter,
    count: items.filter((item) => filter.kinds.includes(item.kind)).length,
  })).filter((filter) => filter.count > 0);
  const activeFilter = filters.find((filter) => filter.key === filterKey);
  const visibleItems = activeFilter ? items.filter((item) => activeFilter.kinds.includes(item.kind)) : items;
  const filterTabs = [
    { value: "all", label: t("share.filter_all"), count: items.length },
    ...filters.map((filter) => ({ value: filter.key, label: t(`share.filters.${filter.key}`), count: filter.count })),
  ];
  const resetDate = formatResetDate(nextResetDate(), lang);

  return (
    <Box sx={g.page}>
      <PreviewSwitcher />

      {/* Setup tasks stay first until they're done (same data as Get Started).
          The preview switcher's `?setup=1` mocks an incomplete setup here only. */}
      <SetupCard weeklyWhenDone={false} mockSetupIncomplete={searchParams.get("setup") === "1"} />

      <Box sx={g.columns}>
        <Box id={TASKS_ID} sx={{ ...g.column(0), ...s.anchor }}>
          <WeeklyTasks
            collapsible={isMobile}
            expanded={open.tasks}
            onToggle={() => toggle("tasks")}
            onEntered={() => handleEntered(TASKS_ID)}
          />
        </Box>

        <Box id={SHARE_GRID_ID} sx={{ ...g.column(2), ...s.anchor }}>
          <SectionCard
            title={t("share.title")}
            subtitle={t("share.subtitle")}
            badge={
              <Box component="span" sx={s.resetBadge}>
                <span className="reset-full">{t("share.resets_on", { date: resetDate })}</span>
                <span className="reset-short">{t("share.resets_on_short", { date: resetDate })}</span>
              </Box>
            }
            collapsible={isMobile}
            expanded={open.share}
            onToggle={() => toggle("share")}
            onEntered={() => handleEntered(SHARE_GRID_ID)}
            isRTL={isRTL}
            bodyId={`${SHARE_GRID_ID}-body`}
          >
            {/* Always rendered, so the list starts on the same line as the other columns. */}
            <Box sx={g.controlRow} role="group" aria-label={t("share.filter_label")}>
              {filters.length > 1 && (
                <Box sx={s.tabs}>
                  <CustomFilterTabs
                    tabs={filterTabs}
                    activeTab={activeFilter ? activeFilter.key : "all"}
                    onChange={(_, value) => setFilterKey(value)}
                    tabItemStyles={g.tabItem}
                    scrollable
                  />
                </Box>
              )}
            </Box>

            <Box sx={s.shareList}>
              {visibleItems.map((item) => (
                <ShareCard key={item.id} item={item} lang={lang} t={t} onShare={(it) => setShareItemId(it.id)} />
              ))}
            </Box>
          </SectionCard>
        </Box>

        <Box id={REPLY_HELPER_ID} sx={{ ...g.column(1), ...s.anchor }}>
          <SectionCard
            title={t("ai.title")}
            subtitle={t("ai.subtitle")}
            collapsible={isMobile}
            expanded={open.ai}
            onToggle={() => toggle("ai")}
            onEntered={() => handleEntered(REPLY_HELPER_ID)}
            isRTL={isRTL}
            bodyId={`${REPLY_HELPER_ID}-body`}
          >
            <Box sx={g.controlRow} role="group" aria-label={t("ai.tools_label")}>
              <Box sx={s.tabs}>
                <CustomFilterTabs
                  tabs={AI_TOOLS.map((tool) => ({ value: tool, label: t(`ai.tools.${tool}`) }))}
                  activeTab={aiTool}
                  onChange={(_, value) => setAiTool(value)}
                  tabItemStyles={g.tabItem}
                />
              </Box>
            </Box>

            {aiTool === "reply" ? <ReplyHelper lang={lang} t={t} /> : <AnnouncementTool lang={lang} t={t} />}
          </SectionCard>
        </Box>
      </Box>

      <ShareDialog item={shareItem} lang={lang} t={t} onClose={() => setShareItemId(null)} onShared={markShared} />
    </Box>
  );
};

export default GrowCommunityContainer;
